import b from '@/components/BatchSelection.module.css';
import { ShipmentBatchDocuments } from './ShipmentBatchDocuments';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { useShipmentList } from './api';
import { DeskFrame, ListBar, LoadMore, NoAccess, Pager, PhoneFrame, SearchBox, useQueryState } from './ListParts';
import { ShipmentCard } from './ShipmentCard';
import { DEFAULT_SHIPMENT_COLUMNS, SHIPMENT_COLUMN_OPTIONS, ShipmentsTable } from './ShipmentsTable';
import { ShipmentSidebar, useShipmentAuxCounts } from './ShipmentViews';
import { ReadyOrdersView } from './ReadyOrdersView';
import { exportLogistics } from './exportLogistics';
import s from './ShipmentsPage.module.css';

const PLACEHOLDER = '批次 / 物流单号 / 订单 / 客户 / 产品';
const DESKTOP_PAGE_SIZE = 5;
const TOUCH_PAGE_SIZE = 20;
const COLUMN_STORAGE_KEY = 'v2-shipments-ledger-columns';

function initialColumns() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLUMN_STORAGE_KEY) || 'null');
    if (Array.isArray(saved)) {
      const valid = SHIPMENT_COLUMN_OPTIONS.map((item) => item.key).filter((key) => saved.includes(key));
      const optionKeys = SHIPMENT_COLUMN_OPTIONS.map((item) => item.key);
      const previousDefaults = optionKeys.filter((key) => key !== 'loss');
      const earlierDefaults = optionKeys.filter((key) => key !== 'loss' && key !== 'out');
      const isPreviousDefault = [previousDefaults, earlierDefaults].some((keys) => valid.length === keys.length && keys.every((key) => valid.includes(key)));
      if (valid.includes('no')) return isPreviousDefault ? DEFAULT_SHIPMENT_COLUMNS : valid;
    }
  } catch { /* storage may be disabled */ }
  return DEFAULT_SHIPMENT_COLUMNS;
}

function ShipmentViewMenu({ onPick }: { onPick: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => { document.removeEventListener('pointerdown', closeOutside); document.removeEventListener('keydown', closeOnEscape); };
  }, [open]);
  const views = [
    ['ready', '待发操作台'], ['today', '今日到期'], ['ledger', '发货批次台账'],
    ['exceptions', '批次含异常'], ['warehouse', '按仓库'],
  ] as const;
  return <div className={s.viewMenu} ref={root}>
    <button type="button" aria-label="切换发货视图" aria-expanded={open} onClick={() => setOpen((value) => !value)}><Icon name="chev" size={14} /></button>
    {open && <div className={s.viewMenuPopup} role="menu" aria-label="发货视图">
      {views.map(([key, label]) => <button type="button" role="menuitem" key={key} onClick={() => { setOpen(false); onPick(key); }}>{label}</button>)}
    </div>}
  </div>;
}

function ColumnSettings({ columns, onChange }: { columns: string[]; onChange: (columns: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return <div className={s.columnSettings} ref={root}>
    <button type="button" className={s.columnTrigger} aria-expanded={open} onClick={() => setOpen((value) => !value)}><Icon name="cog2" />列设置</button>
    {open && <div className={s.columnMenu} role="group" aria-label="显示台账列">
      {SHIPMENT_COLUMN_OPTIONS.map((item) => <label key={item.key}>
        <input type="checkbox" checked={columns.includes(item.key)} disabled={item.key === 'no'} onChange={(event) => onChange(event.target.checked ? [...columns, item.key] : columns.filter((key) => key !== item.key))} />
        {item.title}
      </label>)}
      <button type="button" onClick={() => onChange(DEFAULT_SHIPMENT_COLUMNS)}>恢复设计稿列</button>
    </div>}
  </div>;
}

/**
 * 发货批次台账（/shipments?view=ledger 或 exceptions）。V2 GET 支持日期、仓库和异常筛选。
 * - 手机：根页大标题，搜索常驻（台账主要用来按物流单号反查批次），卡片 + 滚动加载；
 * - iPad / 桌面：表格 + 分页（iPad 稿 S2 就是表格），点行进批次详情。
 */
export function ShipmentsPage() {
  const q = useQueryState();
  const can = useSession((st) => st.can);
  const ledgerView = q.get('view') === 'ledger' || q.get('view') === 'exceptions';
  if (ledgerView && !can('shipments.read')) return <NoAccess what="发货" legacy="/shipments" />;
  return ledgerView ? <ShipmentLedger /> : <ReadyOrdersView />;
}

function ShipmentLedger() {
  const { shortageCount, assemblingCount } = useShipmentAuxCounts();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [columns, setColumns] = useState(initialColumns);
  useEffect(() => {
    try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(columns)); } catch { /* storage may be disabled */ }
  }, [columns]);
  const ff = useFormFactor();
  const q = useQueryState();
  const keyword = q.get('q') || '';
  const period = q.get('period') === 'all' ? 'all' : '30d';
  const warehouseId = q.get('warehouseId') || '';
  const exceptionsOnly = q.get('view') === 'exceptions';
  const page = Math.max(1, Number(q.get('page')) || 1);
  const phone = ff === 'phone';
  const pageSize = ff === 'desktop' ? DESKTOP_PAGE_SIZE : TOUCH_PAGE_SIZE;
  const activePhoneViewRef = useRef<HTMLAnchorElement>(null);
  const list = useShipmentList({ keyword: keyword || undefined, period, warehouseId: warehouseId || undefined, exception: exceptionsOnly ? 'open' : undefined }, pageSize, phone, page);
  const title = exceptionsOnly ? '批次含异常' : '发货批次台账';
  const summary = list.data?.summary;
  const warehouses = list.data?.warehouses || [];

  useEffect(() => { setSelected([]); setSelectionMode(false); }, [keyword, period, warehouseId, exceptionsOnly]);
  const toggle = (id: string) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const exportSelected = async () => {
    if (!selected.length || exporting) return;
    setExporting(true); setExportMessage('');
    try { const count = await exportLogistics({ period, keyword: keyword || undefined, warehouseId: warehouseId || undefined, exception: exceptionsOnly ? 'open' : undefined, ids: selected }); setExportMessage(`已导出 ${count} 个所选批次`); }
    catch (cause) { setExportMessage(cause instanceof Error ? cause.message : '导出失败'); }
    finally { setExporting(false); }
  };

  useLayoutEffect(() => {
    if (!phone) return;
    activePhoneViewRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [phone, exceptionsOnly]);

  const search = (v: string) => q.set({ q: v || undefined, page: undefined });
  const pick = (key: string) => ['ready', 'today', 'ledger', 'exceptions', 'warehouse'].includes(key) && q.set({ view: key === 'ready' ? undefined : key, q: undefined, warehouseId: key === 'warehouse' ? undefined : warehouseId || undefined, page: undefined });
  const openCount = list.items.reduce((a, x) => a + x.exceptions.filter((e) => e.status === 'PENDING' || e.status === 'PROCESSING').length, 0);
  const exportCurrent = async () => {
    if (exporting) return;
    setExporting(true);
    setExportMessage('');
    try {
      const count = await exportLogistics({
        keyword: keyword || undefined, period, warehouseId: warehouseId || undefined,
        exception: exceptionsOnly ? 'open' : undefined,
      });
      setExportMessage(`已导出当前筛选下的 ${count} 个发货批次`);
    } catch (error) {
      setExportMessage(error instanceof Error ? error.message : '导出失败，请重试');
    } finally {
      setExporting(false);
    }
  };
  const dateFilter = <select className={s.filterSelect} aria-label="发货日期范围" value={period} onChange={(event) => q.set({ period: event.target.value === 'all' ? 'all' : undefined, page: undefined })}>
    <option value="30d">近 30 天</option><option value="all">全部日期</option>
  </select>;
  const warehouseFilter = <select className={s.filterSelect} aria-label="筛选仓库" value={warehouseId} onChange={(event) => q.set({ warehouseId: event.target.value || undefined, page: undefined })}>
      <option value="">仓库：全部</option>
      {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
    </select>;
  const filters = <>{dateFilter}{warehouseFilter}</>;

  const body = (() => {
    if (list.error && !list.items.length) return <ErrorState title="发货批次加载失败" message={list.error.message} detail="GET /api/v2/shipments/ledger" onRetry={list.reload} />;
    if (list.loading && !list.items.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      return keyword ? (
        <Empty icon="srch" title={`没有找到「${keyword}」`} action={<Button onClick={() => search('')}>清空搜索</Button>}>
          可以搜批次号、物流单号、物流公司、订单号、客户名称、产品编号或名称。
        </Empty>
      ) : (
        <Empty icon="truck" title={exceptionsOnly ? '所选范围内没有未闭环异常批次' : '所选范围内没有发货批次'}>可调整日期或仓库筛选。</Empty>
      );
    }
    return null;
  })();

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    const phoneViews = [
      { key: 'ready', label: '待发操作台', to: '/shipments' },
      { key: 'today', label: '今日到期', to: '/shipments?view=today' },
      { key: 'shortage', label: '缺货挂起', to: '/orders?view=shortage' },
      { key: 'assembling', label: '等待组装', to: '/orders?view=assembling' },
      { key: 'ledger', label: '发货批次', count: summary?.ledger, to: '/shipments?view=ledger' },
      { key: 'exceptions', label: '批次含异常', count: summary?.openExceptions, to: '/shipments?view=exceptions' },
      { key: 'merge', label: '按客户合单', to: '/shipments?view=merge' },
      { key: 'warehouse', label: '按仓库', to: '/shipments?view=warehouse' },
    ];
    return (
      <PhoneFrame title="作业" actions={<div className={s.phoneHeadActions}><button type="button" aria-label="显示筛选" onClick={() => setPhoneFiltersOpen(true)}><Icon name="srch" size={22} /></button><button type="button" aria-label={selectionMode ? '取消选择批次' : '选择批次'} aria-pressed={selectionMode} onClick={() => { setSelectionMode((value) => !value); setSelected([]); }}><Icon name="select" size={22} /></button></div>} top={<>
        <div className={s.phoneWarehouse}><Icon name="bank" size={14} />{warehouses.find((item) => item.id === warehouseId)?.name || (warehouses.length === 1 ? warehouses[0].name : '全部仓库')}</div>
        <div className={s.phoneViewRow}><div className={s.phoneViews}>{phoneViews.map((view) => <Link key={view.key} ref={(exceptionsOnly ? 'exceptions' : 'ledger') === view.key ? activePhoneViewRef : undefined} className={`${s.phoneChip} ${(exceptionsOnly ? 'exceptions' : 'ledger') === view.key ? s.phoneChipOn : ''}`} to={view.to!}>{view.label}{view.count != null && <span>{view.count}</span>}</Link>)}</div><button type="button" className={s.phoneFilterButton} aria-expanded={phoneFiltersOpen} onClick={() => setPhoneFiltersOpen(!phoneFiltersOpen)}><Icon name="filter" size={16} />筛选</button></div>
        <div className={s.phoneSearch}><SearchBox variant="phone" value={keyword} placeholder={PLACEHOLDER} onSearch={search} />{phoneFiltersOpen && <div className={s.pfilters}>{filters}</div>}</div>
      </>}>
        <ListBar>
          <span>{period === '30d' ? '近 30 天 ' : '全部日期 '}{list.total} 条{openCount > 0 && ` · 本页 ${openCount} 个未闭环异常`}</span>
        </ListBar>
        {selectionMode && <div className={b.toolbar}><span>已选 {selected.length}</span><button type="button" onClick={() => setSelected(list.items.slice(0, 30).map((item) => item.id))}>全选已加载</button><button type="button" disabled={!selected.length || exporting} onClick={() => void exportSelected()}>导出物流</button><button type="button" disabled={!selected.length || selected.length > 30} onClick={() => setDocumentsOpen(true)}>发货单</button></div>}
        {exportMessage && <p role="status" className={b.toolbar}>{exportMessage}</p>}
        {body || list.items.map((sh) => <div key={sh.id} className={b.row}>{selectionMode && <label><input type="checkbox" className={b.check} checked={selected.includes(sh.id)} aria-label={`选择批次 ${sh.shipmentNo}`} onChange={() => toggle(sh.id)} /></label>}<ShipmentCard sh={sh} /></div>)}
        {documentsOpen && <ShipmentBatchDocuments items={list.items.filter((item) => selected.includes(item.id))} onClose={() => setDocumentsOpen(false)} />}
        {!body && <LoadMore hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />}
      </PhoneFrame>
    );
  }

  /* --------------------------- iPad：沿用触控版台账 --------------------------- */
  if (ff === 'tablet') return (
    <DeskFrame
      title={title}
      sub={list.total}
      titleAction={<ShipmentViewMenu onPick={pick} />}
      sidebar={<ShipmentSidebar active={exceptionsOnly ? 'exceptions' : 'ledger'} onPick={pick} shortageCount={shortageCount} assemblingCount={assemblingCount} ledgerCount={summary?.ledger} exceptionCount={summary?.openExceptions} warehouseCount={warehouses.length} />}
      tools={
        <>
          <SearchBox variant="desk" value={keyword} placeholder={PLACEHOLDER} onSearch={search} />
          <select className={s.filterSelect} aria-label="发货日期范围" value={period} onChange={(event) => q.set({ period: event.target.value === 'all' ? 'all' : undefined, page: undefined })}>
            <option value="30d">近 30 天</option><option value="all">全部日期</option>
          </select>
          <ColumnSettings columns={columns} onChange={setColumns} />
          <Button icon="down" onClick={() => void exportCurrent()} disabled={exporting || list.total === 0} disabledReason={exporting ? '正在生成导出文件' : '当前筛选下没有发货批次'}>导出物流</Button>
        </>
      }
      top={<div className={s.ledgerMetaRow}>
        <span>{period === '30d' ? '近 30 天 ' : '全部日期 '}{list.total} 条 · {summary?.openExceptions ?? 0} 条含未闭环异常</span>
        <span className={s.metaSpacer} />
        <select className={s.filterSelect} aria-label="筛选仓库" value={warehouseId} onChange={(event) => q.set({ warehouseId: event.target.value || undefined, page: undefined })}>
          <option value="">仓库：全部</option>
          {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
        </select>
        <Button icon="ref" onClick={list.reload}>刷新</Button>
        <span className={s.sortLabel}>按发货时间倒序</span>
      </div>}
      footer={!body && <Pager page={list.page} pageSize={pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={(p) => q.set({ page: p > 1 ? String(p) : undefined })} />}
    >
      {exportMessage && <p className={s.exportMessage} role="status">{exportMessage}</p>}
      {body || <ShipmentsTable items={list.items} columns={columns} tablet />}
    </DeskFrame>
  );

  /* --------------------------- 桌面：S2 台账与常驻视图栏 --------------------------- */
  return (
    <div className={s.desk}>
      <header className={s.head}>
        <div><h1>{title}</h1><p>{exceptionsOnly ? '有未闭环异常的已出库批次' : '所有已出库批次的完整记录'}<span className="tnum">{period === '30d' ? '近 30 天 · ' : ''}共 {list.total} 条{summary && ' · ' + summary.openExceptions + ' 条含未闭环异常'}</span></p></div>
        <div className={s.headActions}><ColumnSettings columns={columns} onChange={setColumns} /><Button icon="down" onClick={() => void exportCurrent()} disabled={exporting || list.total === 0} disabledReason={exporting ? '正在生成导出文件' : '当前筛选下没有发货批次'}>{exporting ? '导出中…' : '导出物流'}</Button></div>
      </header>
      <div className={s.work}>
        <ShipmentSidebar active={exceptionsOnly ? 'exceptions' : 'ledger'} onPick={pick} shortageCount={shortageCount} assemblingCount={assemblingCount} ledgerCount={summary?.ledger} exceptionCount={summary?.openExceptions} />
        <section className={s.area}>
          <div className={s.toolbar}><SearchBox variant="desk" value={keyword} placeholder={PLACEHOLDER} onSearch={search} />{dateFilter}<Button size="sm" className={s.toolbarExport} onClick={() => void exportCurrent()} disabled={exporting || list.total === 0} disabledReason={exporting ? '正在生成导出文件' : '当前筛选下没有发货批次'}>{exporting ? '导出中…' : '导出'}</Button>{exportMessage && <span className={s.exportMessage} role="status">{exportMessage}</span>}</div>
          {body || <div className={s.body}><ShipmentsTable items={list.items} columns={columns} /></div>}
          {!body && <Pager page={list.page} pageSize={pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={(p) => q.set({ page: p > 1 ? String(p) : undefined })} />}
        </section>
      </div>
    </div>
  );
}
